/**
 * useSmartHome — TanStack Query hook over GET /api/smarthome/status (Wave 1).
 *
 * The backend (smarthome.service) returns an already-aggregated light tally —
 * the hook consumes it directly with no client-side transform. When no Hue/
 * Homey provider is configured the response is { configured:false }, which
 * HomeStatusStrip renders as the "Hjem: alt rolig" empty state.
 *
 * Cadence mirrors useWeather: 60s staleTime + 60s poll. The 'smarthome' WS
 * topic (useSmartHomeWS) invalidates this query on the rare light-state change,
 * so the poll is just a safety net, not the primary freshness path.
 */
import { useQuery } from '@tanstack/react-query';

export type SmartHomeStatus = {
  configured: boolean;
  lights: { total: number; on: number } | null;
  providers: { hue: 'ok' | 'error' | 'off'; homey: 'ok' | 'error' | 'off' };
  fetched_at: string | null;
  stale: boolean;
};

export function useSmartHome() {
  return useQuery<SmartHomeStatus>({
    queryKey: ['smarthome', 'status'],
    queryFn: async () => {
      const r = await fetch('/api/smarthome/status', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`smarthome_fetch_failed: ${r.status}`);
      return (await r.json()) as SmartHomeStatus;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}
